import React, { useState } from 'react';
import { Box, TextField, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { tr, enUS } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';

const DateRangeFilter = ({ filter, onFilterChange }) => {
  const { t, i18n } = useTranslation();
  const [startDate, setStartDate] = useState(filter.value?.startDate || null);
  const [endDate, setEndDate] = useState(filter.value?.endDate || null);

  // Update local state when filter value changes (e.g., when clearing filters)
  React.useEffect(() => {
    setStartDate(filter.value?.startDate || null);
    setEndDate(filter.value?.endDate || null);
  }, [filter.value]);

  const handleStartDateChange = (newValue) => {
    setStartDate(newValue);
    
    // If end date is before new start date, clear end date
    let updatedEndDate = endDate;
    if (newValue && endDate && endDate < newValue) {
      updatedEndDate = null;
      setEndDate(null);
    }
    
    const newValueObj = {
      startDate: newValue,
      endDate: updatedEndDate
    };
    onFilterChange(filter.id, newValueObj);
  };

  const handleEndDateChange = (newValue) => {
    // Validate that end date is not before start date
    if (newValue && startDate && newValue < startDate) {
      return; // Don't update if end date is before start date
    }
    
    setEndDate(newValue);
    const newValueObj = {
      startDate: startDate,
      endDate: newValue
    };
    onFilterChange(filter.id, newValueObj);
  };

  const locale = i18n.language === 'tr' ? tr : enUS;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 350 }}>
      <Typography variant="body2" sx={{ whiteSpace: 'nowrap', color: 'text.secondary', fontWeight: 500 }}>
        {filter.label}:
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={locale}>
          <DatePicker
            label={t('table.filters.startDate')}
            value={startDate}
            onChange={handleStartDateChange}
            maxDate={endDate} // Start date cannot be after end date
            views={['year', 'month', 'day']}
            openTo="day"
            slotProps={{
              textField: {
                size: 'small',
                sx: { minWidth: 150 },
                placeholder: 'dd.mm.yyyy'
              }
            }}
            format="dd.MM.yyyy"
          />
        </LocalizationProvider>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          -
        </Typography>
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={locale}>
          <DatePicker
            label={t('table.filters.endDate')}
            value={endDate}
            onChange={handleEndDateChange}
            minDate={startDate} // End date cannot be before start date
            views={['year', 'month', 'day']}
            openTo="day"
            slotProps={{
              textField: {
                size: 'small',
                sx: { minWidth: 150 },
                placeholder: 'dd.mm.yyyy'
              }
            }}
            format="dd.MM.yyyy"
          />
        </LocalizationProvider>
      </Box>
    </Box>
  );
};

export default DateRangeFilter;
